import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getPublicSettings, membershipCheckout, money, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Motion";

const BENEFITS = [
  "Subscriber-exclusive content and vault assets",
  "Early access to selected releases",
  "Selected exclusive music and visuals",
  "Member discount on eligible store products",
  "Member-only offers",
];

export default function InnerCircle() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [discount, setDiscount] = useState(10);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Inner Circle — Motion & Meaning";
    getPublicSettings().then((s) => { setPlans(s.membershipPlans || []); setDiscount(s.memberDiscountPercent); }).catch(() => {});
  }, []);

  const join = async (plan) => {
    if (!user) return navigate("/login?next=/inner-circle");
    setBusy(true);
    try {
      const res = await membershipCheckout({ planKey: plan.planKey, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 md:pt-44 pb-32 min-h-screen" data-testid="inner-circle-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">PAID MEMBERSHIP — SEPARATE FROM THE MAILING LIST</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light leading-none text-white">
            THE INNER<br /><span className="italic text-gold">CIRCLE.</span>
          </h1>
          <p className="text-white/55 max-w-xl mt-8 leading-relaxed">
            Closer to the source. Membership supports the work directly and opens the inner layer of the
            universe. Purchased music is always yours — membership adds what only members can reach.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-10 mt-20 max-w-4xl">
          <Reveal>
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-6">MEMBERSHIP INCLUDES</p>
              <ul className="space-y-4">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex gap-4 text-white/70"><span className="text-gold">◆</span>{b.includes("discount") ? `${discount}% member discount on eligible store products` : b}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {plans.length === 0 && <p className="text-white/40">Membership enrollment is not open right now.</p>}
            {plans.map((p) => (
              <div key={p.planKey} className="border border-gold/30 p-10" data-testid={`plan-${p.planKey}`}>
                <p className="font-mono text-[10px] tracking-[0.35em] text-gold">{p.name.toUpperCase()}</p>
                <p className="font-serif text-6xl font-light text-white mt-6">
                  {money(p.price, p.currency)}<span className="text-lg text-white/40">/month</span>
                </p>
                <button onClick={() => join(p)} disabled={busy} data-testid={`join-${p.planKey}`}
                  className="mt-10 w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors disabled:opacity-50">
                  {busy ? "OPENING CHECKOUT…" : user ? "JOIN THE INNER CIRCLE" : "SIGN IN TO JOIN"}
                </button>
                <p className="font-mono text-[8px] tracking-[0.2em] text-white/30 mt-5 text-center">
                  CANCEL ANYTIME · PURCHASED MUSIC IS YOURS FOREVER
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
